import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiEndpoint } from '../consts';
import fetchWithAuth from '../apis';
import { extractApiErrorDetail } from '../api-error';

/** One materialized schedule row — mirrors backend ScheduledJobView. */
export interface ScheduledJob {
  id: number;
  job_name: string;
  scope: string; // "global" | "per_org"
  organization_id: number | null;
  schedule: string; // a cron, or the "INHERIT_ORG" sentinel
  timezone: string;
  queue: string;
  enabled: boolean;
  overlap_policy: string;
  catchup_policy: string;
  max_duration_sec: number;
  next_run_at: string | null;
  last_run_at: string | null;
}

export interface JobConfigUpdate {
  schedule?: string;
  enabled?: boolean;
  queue?: string;
  timezone?: string;
  overlap_policy?: string;
  catchup_policy?: string;
  max_duration_sec?: number;
}

export interface OrgScheduleUpdate {
  timezone?: string;
  base_schedule?: string;
}

/** List scheduled jobs, optionally filtered to one organization. */
export function useSchedulerJobsQuery(organizationId?: number | null) {
  const params = new URLSearchParams();
  if (organizationId != null) {
    params.append('organization_id', String(organizationId));
  }
  const queryString = params.toString();
  return useQuery({
    queryKey: ['scheduler', 'jobs', organizationId ?? 'all'],
    queryFn: async () => {
      const response = await fetchWithAuth(
        `${apiEndpoint}/admin/scheduler/jobs${queryString ? `?${queryString}` : ''}`,
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(extractApiErrorDetail(error.detail) || 'Failed to load scheduled jobs');
      }
      return response.json() as Promise<ScheduledJob[]>;
    },
  });
}

/** Override a single job's schedule / enabled / queue / policies / timezone. */
export function useUpdateJobMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, update }: { jobId: number; update: JobConfigUpdate }) => {
      const response = await fetchWithAuth(`${apiEndpoint}/admin/scheduler/jobs/${jobId}`, {
        method: 'PUT',
        body: JSON.stringify(update),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(extractApiErrorDetail(error.detail) || 'Failed to update job');
      }
      return response.json() as Promise<ScheduledJob>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduler'] }),
  });
}

/** Fire a job once, immediately (trigger=manual). */
export function useRunJobMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) => {
      const response = await fetchWithAuth(`${apiEndpoint}/admin/scheduler/jobs/${jobId}/run`, {
        method: 'POST',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(extractApiErrorDetail(error.detail) || 'Failed to start manual run');
      }
      return response.json() as Promise<{ job_run_id: number }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduler'] }),
  });
}

/** Set an organization's timezone + base schedule (its inheriting jobs recompute). */
export function useUpdateOrgScheduleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, update }: { orgId: number; update: OrgScheduleUpdate }) => {
      const response = await fetchWithAuth(`${apiEndpoint}/admin/scheduler/orgs/${orgId}/schedule`, {
        method: 'PUT',
        body: JSON.stringify(update),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(extractApiErrorDetail(error.detail) || 'Failed to update organization schedule');
      }
      return response.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduler'] }),
  });
}
